import { forwardRef, type ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

export interface FeatureTriadItem {
  icon: LucideIcon;
  title: ReactNode;
  body: ReactNode;
  footnote?: ReactNode;
}

export interface FeatureTriadProps {
  items: [FeatureTriadItem, FeatureTriadItem, FeatureTriadItem];
  className?: string;
  variant?: "bordered" | "minimal";
}

export const FeatureTriad = forwardRef<HTMLDivElement, FeatureTriadProps>(function FeatureTriad(
  { items, className, variant = "bordered" },
  ref,
) {
  return (
    <div
      ref={ref}
      className={cn("grid grid-cols-1 gap-px md:grid-cols-3", variant === "bordered" && "bg-rule", className)}
    >
      {items.map((it, i) => {
        const Icon = it.icon;
        return (
          <div
            key={i}
            className={cn(
              variant === "bordered" ? "bg-background p-8 md:p-10" : "p-2 md:p-4",
            )}
          >
            <Icon className="h-6 w-6 text-accent-press" strokeWidth={1.5} aria-hidden />
            <h3 className="mt-6 text-display text-xl tracking-tight">{it.title}</h3>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{it.body}</p>
            {it.footnote && <p className="mt-4 dateline">{it.footnote}</p>}
          </div>
        );
      })}
    </div>
  );
});
